'use client';

import type { PostCardProps } from '@routeroyal/theme-core';
import Link from 'next/link';
import Image from 'next/image';

/**
 * AllysPostCard - Allys-themed post card
 * Displays a post with the Allys navy/gold color scheme and Inter font
 */
export function AllysPostCard({
  post,
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  showDate = true,
  showReadingTime = true,
  showTags = false,
  locale = 'en',
  className = '',
  basePath = '/blog',
  messages,
}: PostCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';
  const isHorizontal = variant === 'horizontal';

  // Build the post URL
  const postUrl = `/${locale}${basePath}/${post.slug}`;

  // Format the date based on locale
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format reading time
  const formatReadingTime = (minutes?: number) => {
    if (!minutes) return '';
    const label = messages?.minuteRead || 'min read';
    return `${minutes} ${label}`;
  };

  // Horizontal variant layout
  if (isHorizontal) {
    return (
      <article
        className={`
          group relative bg-white rounded-2xl overflow-hidden
          border border-gray-200 hover:border-[#BBA339]/30
          hover:shadow-xl transition-all duration-300
          flex flex-col sm:flex-row
          [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          ${className}
        `}
      >
        <Link href={postUrl} className="flex flex-col sm:flex-row w-full">
          {/* Image */}
          {post.featuredImage && (
            <div className="relative w-full sm:w-48 md:w-64 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
              <Image
                src={post.featuredImage.url}
                alt={post.featuredImage.alt || post.title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                sizes="(max-width: 640px) 100vw, 256px"
              />
              {showCategory && post.category && (
                <div className="absolute top-3 left-3 sm:hidden">
                  <span className="px-2 py-1 text-xs font-semibold text-white bg-[#243462] backdrop-blur-sm rounded-full">
                    {post.category.name}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 p-4 sm:p-5 flex flex-col justify-center">
            {showCategory && post.category && (
              <div className="hidden sm:block mb-2">
                <span className="text-xs font-semibold text-[#BBA339] uppercase tracking-wide">
                  {post.category.name}
                </span>
              </div>
            )}

            <h3 className="text-lg font-bold mb-2 line-clamp-2 text-[#243462] group-hover:text-[#BBA339] transition-colors">
              {post.title}
            </h3>

            {showExcerpt && post.excerpt && (
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {post.excerpt}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-auto">
              {showAuthor && (
                <span className="font-medium text-[#243462]">{post.author.name}</span>
              )}
              {showAuthor && showDate && <span>•</span>}
              {showDate && post.publishedAt && (
                <time dateTime={post.publishedAt}>
                  {formatDate(post.publishedAt)}
                </time>
              )}
              {showReadingTime && post.readingTime && (
                <>
                  <span>•</span>
                  <span className="text-[#243462] font-medium">{formatReadingTime(post.readingTime)}</span>
                </>
              )}
            </div>
          </div>
        </Link>
      </article>
    );
  }

  // Default/Compact/Featured variant layout
  return (
    <article
      className={`
        group relative bg-white rounded-2xl overflow-hidden
        border border-gray-200 hover:border-[#BBA339]/30
        hover:shadow-xl transition-all duration-300
        [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
        ${className}
      `}
    >
      <Link href={postUrl} className="block">
        {/* Featured Image */}
        {post.featuredImage && (
          <div
            className={`
              relative overflow-hidden
              ${isFeatured ? 'h-80 md:h-96' : isCompact ? 'h-40' : 'h-52 md:h-60'}
            `}
          >
            <Image
              src={post.featuredImage.url}
              alt={post.featuredImage.alt || post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes={
                isFeatured
                  ? '(max-width: 768px) 100vw, 800px'
                  : '(max-width: 768px) 100vw, 400px'
              }
            />
            {showCategory && post.category && (
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 text-xs font-semibold text-white bg-[#243462] backdrop-blur-sm rounded-full">
                  {post.category.name}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className={`p-6 ${isFeatured ? 'lg:p-8' : isCompact ? 'p-4' : ''}`}>
          {/* Category (when no image) */}
          {!post.featuredImage && showCategory && post.category && (
            <div className="mb-2">
              <span className="text-xs font-semibold text-[#BBA339] uppercase tracking-wide">
                {post.category.name}
              </span>
            </div>
          )}

          {/* Title */}
          <h3
            className={`
              font-bold mb-3 line-clamp-2 text-[#243462] group-hover:text-[#BBA339] transition-colors
              ${isFeatured ? 'text-2xl lg:text-3xl' : isCompact ? 'text-base' : 'text-lg md:text-xl'}
            `}
          >
            {post.title}
          </h3>

          {/* Excerpt */}
          {showExcerpt && post.excerpt && !isCompact && (
            <p
              className={`
                text-gray-600 mb-4 line-clamp-3
                ${isFeatured ? 'text-base md:text-lg' : 'text-sm'}
              `}
            >
              {post.excerpt}
            </p>
          )}

          {/* Tags */}
          {showTags && post.tags && post.tags.length > 0 && !isCompact && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="px-2 py-0.5 text-xs bg-[#BBA339]/10 text-[#243462] rounded font-medium"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          )}

          {/* Meta Information */}
          <div
            className={`
              flex flex-wrap items-center gap-3 text-gray-500
              ${isFeatured ? 'text-sm' : 'text-xs'}
            `}
          >
            {showAuthor && (
              <div className="flex items-center gap-2">
                {post.author.avatar && (
                  <div
                    className={`
                      relative rounded-full overflow-hidden ring-1 ring-[#BBA339]/20
                      ${isFeatured ? 'w-8 h-8' : 'w-6 h-6'}
                    `}
                  >
                    <Image
                      src={post.author.avatar}
                      alt={post.author.name}
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
                <span className="font-medium text-[#243462]">{post.author.name}</span>
              </div>
            )}
            
            {showAuthor && showDate && <span>•</span>}
            
            {showDate && post.publishedAt && (
              <time dateTime={post.publishedAt} className="text-gray-600">
                {formatDate(post.publishedAt)}
              </time>
            )}
            
            {showReadingTime && post.readingTime && (
              <>
                <span>•</span>
                <span className="text-[#243462] font-medium">{formatReadingTime(post.readingTime)}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
